import { Alert, AlertIcon, Button, CloseButton, Flex, FormHelperText, Input } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'

import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { LoadingBlock } from '../../../components/Loading'
import { MoneyInput } from '../../../components/MaskedInput'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { useUser } from '../../../contexts/UserContext'
import { useHasRole, useAppToast } from '../../../helpers'
import {
  IConsultantOption,
  ICourseOption,
  IUserOption,
  ONLINE_PAYMENT_METHOD_LABELS,
  OnlinePaymentMethod,
  PAYMENT_METHOD_LABELS,
  PaymentMethod,
  RECEIPT_REFERENCE_LABELS,
  ReceiptReference,
  SALE_CONDITION_LABELS,
  SALE_PLATFORM_LABELS,
  SaleCondition,
  SalePlatform,
  SalesReceiptService
} from '../../../services/SalesReceipt/SalesReceiptService'

interface ISalesReceiptEditModalProps {
  id: number | null
  onClose: (updated?: boolean) => void
}

const isCard = (method: string) => method === 'debit' || method === 'credit_machine'

// Who sold the receipt: a consultant, or a panel user on a contract extension (ABRA-388).
interface ISellerOption {
  id: number
  name: string
}

export default function SalesReceiptEditModal({ id, onClose }: ISalesReceiptEditModalProps) {
  const service = new SalesReceiptService()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const { id: userId, commercialConsultantId } = useUser()
  const canEditAll = hasRole('edit_sales_receipts')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [sellers, setSellers] = useState<ISellerOption[]>([])
  const [confirmingGiveAway, setConfirmingGiveAway] = useState(false)
  // The receipt's kind is fixed once submitted (online vs presencial, enrollment vs contract
  // extension); it drives the field set and the seller list.
  const [isOnline, setIsOnline] = useState(false)
  const [isExtension, setIsExtension] = useState(false)
  const [courses, setCourses] = useState<ICourseOption[]>([])
  const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([])
  // Name kept on receipts submitted before the consultant link existed (or whose name
  // never matched a consultant, or whose user was removed); shown as a hint so the editor
  // knows who to pick.
  const [unlinkedName, setUnlinkedName] = useState('')
  // Presencial: uma ou mais matrículas por comprovante. (Online usa form.registration_number.)
  const [matriculas, setMatriculas] = useState<string[]>([''])
  const [form, setForm] = useState({
    // commercial_consultant_id, or user_id on a contract extension.
    seller_id: '',
    registration_number: '',
    receipt_reference: 'matricula' as ReceiptReference,
    sale_condition: 'a_vista' as SaleCondition,
    payment_method: '' as PaymentMethod | OnlinePaymentMethod | '',
    stone_id: '',
    student_email: '',
    sale_platform: '' as SalePlatform | '',
    value: null as number | null
  })

  // Ajuste de estado durante a render, e não num efeito: o efeito só roda **depois da pintura**,
  // então na transição `null -> id` o primeiro quadro sairia com `loading=false` e com os dados do
  // registro anterior ainda na mão. O React refaz a render antes de pintar quando o `set` acontece
  // aqui, então o esqueleto aparece já na abertura. Fechar (`id -> null`) de propósito não mexe em
  // `loading`: é o que mantém o conteúdo na tela durante a animação de saída.
  const [openedId, setOpenedId] = useState(id)
  if (id !== openedId) {
    setOpenedId(id)
    if (id !== null) setLoading(true)
  }

  useEffect(() => {
    if (id === null) return

    const fetch = async () => {
      try {
        // Degrade instead of aborting the whole modal if a seller list fails to load: the
        // receipt still opens and its own seller stays selectable.
        const [receipt, consultantOptions, userOptions] = await Promise.all([
          service.get(id),
          service.getConsultants().catch(() => [] as IConsultantOption[]),
          service.getUsers().catch(() => [] as IUserOption[])
        ])

        const extension = receipt.receipt_type === 'contract_extension'
        setIsExtension(extension)

        // The endpoints only list consultants shown on forms and active "Experiência do
        // Aluno" users; keep the receipt's own seller selectable even if it has since been
        // hidden or left the group.
        const options: ISellerOption[] = extension ? userOptions : consultantOptions
        const sellerId = extension ? receipt.user_id : receipt.commercial_consultant_id
        const isLinked = sellerId !== null
        const isListed = options.some((option) => option.id === sellerId)
        setSellers(
          isLinked && !isListed
            ? [...options, { id: sellerId, name: receipt.consultant_name }].sort((a, b) =>
                a.name.localeCompare(b.name, 'pt-BR')
              )
            : options
        )
        setUnlinkedName(isLinked ? '' : receipt.consultant_name)

        const online = receipt.sale_platform !== null
        setIsOnline(online)
        if (online) {
          setSelectedCourseIds((receipt.courses ?? []).map((c) => c.id))
          // Keep the receipt's own courses selectable even if one is now disabled/removed.
          service
            .getCourses()
            .then((list) => {
              const merged = [...list]
              ;(receipt.courses ?? []).forEach((c) => {
                if (!merged.some((m) => m.id === c.id)) merged.push({ id: c.id, name: c.name })
              })
              setCourses(merged.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')))
            })
            .catch(() => setCourses(receipt.courses ?? []))
        }

        // registration_number é um array JSON; o presencial edita a lista em `matriculas`,
        // o online usa o primeiro (e único) elemento em form.registration_number.
        setMatriculas(receipt.registration_number?.length ? receipt.registration_number : [''])

        setForm({
          seller_id: isLinked ? String(sellerId) : '',
          registration_number: receipt.registration_number?.[0] ?? '',
          receipt_reference: receipt.receipt_reference ?? 'matricula',
          sale_condition: receipt.sale_condition ?? 'a_vista',
          payment_method: receipt.payment_method ?? '',
          stone_id: receipt.stone_id || '',
          student_email: receipt.student_email ?? '',
          sale_platform: receipt.sale_platform ?? '',
          value: receipt.value != null ? Number(receipt.value) : null
        })
      } catch (e) {
        toast.error('Erro ao carregar comprovante', e)
        onClose()
      } finally {
        setLoading(false)
      }
    }
    fetch()
    // Loads once per receipt: service/toast/onClose are recreated every render and
    // would refetch on each one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const addMatricula = () => setMatriculas((prev) => [...prev, ''])
  const removeMatricula = (index: number) => setMatriculas((prev) => prev.filter((_, i) => i !== index))
  const updateMatricula = (index: number, value: string) =>
    setMatriculas((prev) => prev.map((m, i) => (i === index ? value.replace(/\D/g, '') : m)))

  // Quem só pode editar os seus comprovantes perde o acesso a este ao passá-lo para
  // outro consultor — avisa antes de salvar (ABRA-140).
  // On a contract extension the owner is the account itself, not a linked consultant (ABRA-388).
  const ownSellerId = isExtension ? (userId !== null ? Number(userId) : null) : commercialConsultantId
  const isGivingAway = !canEditAll && ownSellerId !== null && !!form.seller_id && Number(form.seller_id) !== ownSellerId

  const handleSave = () => {
    if (!form.seller_id) {
      toast.warning('Selecione o consultor')
      return
    }
    if (isOnline) {
      if (
        !form.registration_number ||
        !form.student_email ||
        selectedCourseIds.length === 0 ||
        !form.sale_platform ||
        !form.value
      ) {
        toast.warning('Preencha todos os campos obrigatórios')
        return
      }
      if (form.sale_platform === 'internal_sale' && !form.payment_method) {
        toast.warning('Informe a forma de pagamento')
        return
      }
    } else {
      if (matriculas.filter(Boolean).length === 0 || !form.value || !form.payment_method) {
        toast.warning('Preencha todos os campos obrigatórios')
        return
      }
      if (isCard(form.payment_method) && !form.stone_id) {
        toast.warning('Informe o Stone ID para pagamentos no cartão')
        return
      }
    }
    // Handing the receipt to another consultant is irreversible for an own-only editor,
    // so confirm before the single-click save (a native <select> is easy to nudge).
    if (isGivingAway) {
      setConfirmingGiveAway(true)
      return
    }
    doSave()
  }

  const doSave = async () => {
    if (id === null) return

    setConfirmingGiveAway(false)
    setSaving(true)
    const value = form.value ?? 0
    try {
      if (isOnline) {
        await service.update(id, {
          commercial_consultant_id: Number(form.seller_id),
          registration_number: [form.registration_number],
          student_email: form.student_email,
          course_ids: selectedCourseIds,
          sale_platform: form.sale_platform as SalePlatform,
          payment_method: form.sale_platform === 'internal_sale' ? (form.payment_method as OnlinePaymentMethod) : null,
          value
        })
      } else {
        const fields = {
          registration_number: matriculas.map((m) => m.trim()).filter(Boolean),
          receipt_reference: form.receipt_reference,
          sale_condition: form.sale_condition,
          payment_method: form.payment_method as PaymentMethod,
          stone_id: isCard(form.payment_method) ? form.stone_id : null,
          value
        }
        const sellerId = Number(form.seller_id)
        await service.update(
          id,
          isExtension ? { ...fields, user_id: sellerId } : { ...fields, commercial_consultant_id: sellerId }
        )
      }
      toast.success('Comprovante atualizado com sucesso')
      onClose(true)
    } catch (e) {
      const err = e as AxiosError<{ message?: string }>
      toast.error(err.response?.data?.message ?? 'Erro ao atualizar comprovante', e)
    } finally {
      setSaving(false)
    }
  }

  const renderPresencialFields = () => (
    <>
      <FormField label="Nº Matrícula" mb="3" isRequired>
        {matriculas.map((matricula, index) => (
          <Flex key={index} gap="2" mb="2" align="center">
            <Input
              inputMode="numeric"
              value={matricula}
              placeholder={`Matrícula ${index + 1}`}
              onChange={(e) => updateMatricula(index, e.target.value)}
            />
            {matriculas.length > 1 && (
              <CloseButton onClick={() => removeMatricula(index)} aria-label="Remover matrícula" />
            )}
          </Flex>
        ))}
        <Button variant="link" colorScheme="magenta" size="sm" onClick={addMatricula}>
          + Adicionar matrícula
        </Button>
      </FormField>
      <FormField label="Comprovante referente a" mb="3" isRequired>
        <FormSelect
          value={form.receipt_reference}
          onChange={(value) => setForm({ ...form, receipt_reference: value as ReceiptReference })}
          options={Object.entries(RECEIPT_REFERENCE_LABELS).map(([value, label]) => ({
            value: value,
            label: label
          }))}
          hideEmptyOption
        />
      </FormField>
      <FormField label="Forma de Pagamento do Contrato" mb="3" isRequired>
        <FormSelect
          value={form.sale_condition}
          onChange={(value) => setForm({ ...form, sale_condition: value as SaleCondition })}
          options={Object.entries(SALE_CONDITION_LABELS).map(([value, label]) => ({
            value: value,
            label: label
          }))}
          hideEmptyOption
        />
      </FormField>
      <FormField label="Forma de Pagamento" mb="3" isRequired>
        <FormSelect
          value={form.payment_method}
          onChange={(value) => setForm({ ...form, payment_method: value as PaymentMethod })}
          options={Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({
            value: value,
            label: label
          }))}
          hideEmptyOption
        />
      </FormField>
      {isCard(form.payment_method) && (
        <FormField label="Stone ID" mb="3" isRequired>
          <Input value={form.stone_id} onChange={(e) => setForm({ ...form, stone_id: e.target.value })} />
        </FormField>
      )}
    </>
  )

  const renderOnlineFields = () => (
    <>
      <FormField label="Nº da Fatura / Pedido" mb="3" isRequired>
        <Input
          value={form.registration_number}
          onChange={(e) => setForm({ ...form, registration_number: e.target.value })}
        />
      </FormField>
      <FormField label="E-mail do Aluno" mb="3" isRequired>
        <Input
          type="email"
          value={form.student_email}
          onChange={(e) => setForm({ ...form, student_email: e.target.value })}
        />
      </FormField>
      <FormField label="Curso" mb="3" isRequired>
        <SearchableSelect<number>
          isMulti
          placeholder="Selecione um ou mais cursos"
          options={courses.map((c) => ({ value: c.id, label: c.name }))}
          value={selectedCourseIds}
          onChange={setSelectedCourseIds}
          entity="curso"
        />
      </FormField>
      <FormField label="Plataforma da Venda" mb="3" isRequired>
        <FormSelect
          value={form.sale_platform}
          onChange={(value) => setForm({ ...form, sale_platform: value as SalePlatform, payment_method: '' })}
          options={Object.entries(SALE_PLATFORM_LABELS).map(([value, label]) => ({
            value: value,
            label: label
          }))}
          hideEmptyOption
        />
      </FormField>
      {form.sale_platform === 'internal_sale' && (
        <FormField label="Forma de Pagamento" mb="3" isRequired>
          <FormSelect
            value={form.payment_method}
            onChange={(value) => setForm({ ...form, payment_method: value as OnlinePaymentMethod })}
            options={Object.entries(ONLINE_PAYMENT_METHOD_LABELS).map(([value, label]) => ({
              value: value,
              label: label
            }))}
            hideEmptyOption
          />
        </FormField>
      )}
    </>
  )

  return (
    <>
      <FormModal
        isOpen={id !== null}
        onClose={() => onClose()}
        title="Editar Comprovante de Venda"
        footer={
          <>
            <Button variant="ghost" mr="3" onClick={() => onClose()}>
              Cancelar
            </Button>
            <Button colorScheme="magenta" onClick={handleSave} isLoading={saving} isDisabled={loading}>
              Salvar
            </Button>
          </>
        }
      >
        {loading ? (
          <LoadingBlock />
        ) : (
          <>
            <FormField label="Nome do Consultor" mb="3" isRequired isInvalid={!form.seller_id}>
              <FormSelect
                value={form.seller_id}
                onChange={(value) => setForm({ ...form, seller_id: value })}
                options={sellers.map((seller) => ({ value: seller.id, label: seller.name }))}
                placeholder="Selecione o consultor"
              />
              {unlinkedName && !form.seller_id && (
                <FormHelperText color="orange.500">
                  Este comprovante foi enviado como &quot;{unlinkedName}&quot; e ainda não está vinculado a um
                  consultor. Selecione o consultor correspondente.
                </FormHelperText>
              )}
              {isGivingAway && (
                <Alert status="warning" mt="2" fontSize="sm" borderRadius="md">
                  <AlertIcon />
                  Ao salvar, este comprovante passa a ser de outro consultor e você não poderá mais editá-lo.
                </Alert>
              )}
            </FormField>

            {isOnline ? renderOnlineFields() : renderPresencialFields()}

            <FormField label="Valor" mb="3" isRequired>
              <MoneyInput value={form.value} onChange={(value) => setForm({ ...form, value })} />
            </FormField>
          </>
        )}
      </FormModal>
      <ConfirmDialog
        isOpen={confirmingGiveAway}
        onClose={() => setConfirmingGiveAway(false)}
        onConfirm={doSave}
        action="save"
        title="Passar comprovante para outro consultor?"
        description="Este comprovante deixará de ser seu e você não poderá mais editá-lo. Deseja continuar?"
        confirmLabel="Sim, salvar"
      />
    </>
  )
}
